import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

import { LANDING_MEDIA } from './landingMedia';
import { HERO_HEADING } from './landingStyles';
import { type ListingSuggestion } from './listingSearch';
import PropertySearchForm from './PropertySearchForm';

const Hero = ({ onSubmit }: { onSubmit: (listing: ListingSuggestion) => void }) => (
  // Full-bleed and square: the photo runs edge to edge, sits flush under the
  // site header, and dissolves into the page rather than ending on an edge.
  // The copy column lines up with the header's own container.
  <Box as="section" className="relative w-full justify-center overflow-hidden bg-od-bg-secondary">
    {/* Decorative, and anchored to the bottom so the crop comes off the sky. */}
    <Image
      src={LANDING_MEDIA.heroBackground.src}
      alt=""
      aria-hidden
      fetchPriority="high"
      className="absolute inset-0 size-full object-cover object-bottom"
    />
    {/* Washes the page ground over the foot of the photo, so the hero has no
        visible bottom edge. The gradient has to reach full opacity before the
        very bottom, or the photo shows through against the section below. */}
    <Box
      aria-hidden
      className="absolute inset-x-0 bottom-0 h-[26%] bg-linear-to-b from-od-bg-secondary/0 to-od-bg-secondary to-80%"
    />
    <Box className="relative w-full max-w-[1200px] px-6 py-16 lg:min-h-[628px] lg:items-center lg:px-10 lg:py-0">
      <Col className="w-full max-w-[562px] gap-8">
        <Col className="w-full gap-5">
          {/* The design breaks after "for"; the block span pins that at md+,
              and text-balance keeps narrower widths from stranding a word. */}
          <Text as="h1" className={cn(HERO_HEADING, 'text-balance text-od-content-primary')}>
            Submit an offer for <span className="md:block">your client</span>
          </Text>
          <Text className="text-[20px] tracking-[-1px] text-od-content-secondary">
            Submit in minutes, with full visibility from offer to close
          </Text>
        </Col>
        <Box className="w-full">
          <PropertySearchForm onSubmit={onSubmit} />
        </Box>
        <Row className="flex-wrap items-center gap-1">
          <Text className="text-[20px] tracking-[-1px] text-od-content-secondary">
            Have a client that&rsquo;s selling?
          </Text>
          <Link
            href="/agents"
            // Rails-owned path with no cosmos route; a plain anchor spares the
            // client router a failed lookup before its hard-navigation fallback.
            // oxlint-disable-next-line jsx-a11y/anchor-is-valid -- Link merges in the href
            render={<a aria-label="Request a cash offer for your client" />}
            className="text-[20px] tracking-[-1px] text-od-content-accent-brand underline"
            analyticsName="cosmos-make-offer-landing-cash-offer"
          >
            Request a cash offer
          </Link>
        </Row>
      </Col>
    </Box>
  </Box>
);

export default Hero;
